import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_LOCATIONS_FORM_FIELDS,
  EDIT_LOCATIONS_FORM_FIELDS,
  LOCATIONS_FILTER_COLUMNS,
  LOCATIONS_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformLocationResponseRow,
  transformLocationUpdateBody,
  transformLocationsRows,
} from "../util";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import EditRow from "../components/dataTable/EditRow";
import TableActionButton from "../ui/TableActionButton";
import { deleteLocation, getLocationByRef, updateLocationByRef } from "../api";
import { INITIAL_LOCATION_EDIT_STATE } from "../constants/editState";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_LOCATION_TRANSLATIONS,
  READ_LOCATION_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "IWF ID"?: string | null;
  "IWF Location Name"?: string | null;
  "IWF Domain Name"?: string | null;
}

const INITIAL_STATE = {
  "IWF ID": "",
  "IWF Location Name": "",
  "IWF Domain Name": "",
};

const searchFields = ["IWF ID", "IWF Location Name", "IWF Domain Name"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_LOCATIONS_FORM_FIELDS
);

const Locations = () => {
  const {
    token,
    scopes: { locations },
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  useAppTitle("Locations- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [locationData, setLocationsData] = useState<any[]>([]);
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [selectedColumn, setSelectedColumn] = useState<{
    id: string;
    value: string;
  } | null>(null);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const { data, pageNum, pageSize, sortByField, sortDirection } =
        searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
        ...(sortByField && { sortByField }),
        ...(sortDirection && { sortDirection }),
        ...(data &&
          data["IWF ID"] && {
            "IWF-ID": data["IWF ID"],
          }),
        ...(data &&
          data["IWF Location Name"] && {
            "IWF-LOCATION-NAME": data["IWF Location Name"],
          }),
        ...(data &&
          data["IWF Domain Name"] && {
            "IWF-DOMAIN-NAME": data["IWF Domain Name"],
          }),
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/locations?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setLocationsData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const deleteConnection = async (ref: string) => {
    if (token) {
      try {
        await deleteLocation({
          token,
          ref,
        });
        successAlert(DELETE_LOCATION_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchLocations();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_LOCATION_TRANSLATIONS.header}
        pageDescription={READ_LOCATION_TRANSLATIONS.headerSubtext}
        sideComponent={<div className="dropdown-details" />}
      />

      <>
        {showAdvancedSearch ? (
          <AdvancedSearch
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            advancedSearchState={searchData}
            advancedSearchConfig={searchConfigNew}
            setAdvancedSearchState={setSearchData}
            onSubmit={(data) =>
              setSearhQuery({ ...searchQuery, pageNum: 1, pageSize: 20, data })
            }
            onCancel={() => {
              setSearchData(INITIAL_STATE);
              handleToggleAdvanceSearchPanel();
            }}
          />
        ) : null}

        {showSidePanel && selectedConnectionId && !isBulkUploadEnabled ? (
          <EditRow
            panelName={READ_LOCATION_TRANSLATIONS.editRow.panelName}
            initialState={INITIAL_LOCATION_EDIT_STATE}
            org={selectedConnectionId}
            esRef={selectedConnectionId}
            setShowEditPanel={handleToggleSidePanel}
            onDelete={() => deleteConnection(selectedConnectionId)}
            formFields={EDIT_LOCATIONS_FORM_FIELDS}
            header={READ_LOCATION_TRANSLATIONS.editRow.header}
            transformResponseRow={transformLocationResponseRow}
            transformUpdateBody={transformLocationUpdateBody}
            onUpdate={updateLocationByRef}
            loadDataByRef={getLocationByRef}
            hasDeletePermission={locations && locations.includes("d")}
            hasEditPermission={locations && locations.includes("u")}
            refreshTable={fetchLocations}
            deleteModalItemName="IWF-LOCATION-NAME"
          />
        ) : null}

        {showSidePanel && isBulkUploadEnabled ? (
          <BulkUpload
            moduleName="Location"
            pathName="/api/v1/app-config/locations"
            csvTemplateFile={["IWF-ID,IWF-LOCATION-NAME,IWF-DOMAIN-NAME"]}
            closeBulkUploadSidePanel={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(false);
              fetchLocations();
            }}
          />
        ) : null}

        <DataTableWithControls
          loading={loading}
          filterColumns={LOCATIONS_FILTER_COLUMNS}
          selectedColumn={selectedColumn}
          setSelectedColumn={setSelectedColumn}
          onSearch={(term) =>
            setSearhQuery({
              ...searchQuery,
              pageNum: 1,
              pageSize: 20,
              data: { [`${selectedColumn?.id}`]: term },
            })
          }
          setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
          showCreateButton={locations && locations.includes("c")}
          onCreateButtonClick={() => {
            navigate(`/configurationManagement/locations/new`);
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          rows={transformLocationsRows(locationData).map((row: any) => ({
            ...row,
            action: (
              <TableActionButton
                onActionButtonClick={() => {
                  setSelectedConnectionId(row.id);
                  handleToggleSidePanel();
                }}
              />
            ),
          }))}
          numberOfAdvancedSearchFilters={searchConfigNew.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc, el: any) => {
              const { name } = el;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const obj: any = { ...searchData };
              return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
            },
            0
          )}
          clearAdvancedSearchFilters={() => {
            setSearchData(INITIAL_STATE);
            setSearhQuery({
              ...searchQuery,
              pageNum: 1,
              pageSize: 20,
              data: {},
            });
          }}
          headers={LOCATIONS_TABLE_HEADERS}
          pageNum={searchQuery.pageNum}
          setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
          totalItems={numberOfRows}
          totalPages={numberOfPages}
          pageSize={searchQuery.pageSize}
          setPageSize={(pageSize) =>
            setSearhQuery({ ...searchQuery, pageSize })
          }
          sortByField={searchQuery.sortByField}
          sortDirection={searchQuery.sortDirection}
          handleSort={sortTable}
          showBulkUploadButton={locations && locations.includes("c")}
          onBulkUploadButtonClick={() => {
            handleToggleSidePanel();
            setIsBulkUploadEnabled(true);
          }}
        />
      </>
    </>
  );
};

export default Locations;
